// src/app/layout/component/app.topbar.ts
import { Component, inject, OnDestroy, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { HttpClient } from '@angular/common/http';

import { MenuItem } from 'primeng/api';
import { StyleClassModule } from 'primeng/styleclass';
import { ButtonModule } from 'primeng/button';
import { ImageModule } from 'primeng/image';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { PopoverModule } from 'primeng/popover';
import { FormsModule } from '@angular/forms';

import { AppConfigurator } from '../app.configurator';
import { LayoutService } from '../../service/layout.service';
import { LocationDropdownComponent } from '../location-dropdown/location.dropdown';
import { DiseaseDropdownComponent } from '../disease-component/disease.dropdown';
import { LocationSelectionService } from '../../../pages/service/location-selection.service';

import { AnalysisStore } from '../../../core/shared/analysis.store';
import { T3AnalysisStore } from '../../../core/shared/t3-model/t3-analysis.store';
import { LoadingService } from '../../../core/shared/loading.service';
import { MeService } from '../../../core/shared/me.service';

import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { EventType, EventMessage, AuthenticationResult } from '@azure/msal-browser';
import { filter, Subject, takeUntil } from 'rxjs';
import { authoritySUSI } from '../../../pages/auth/msal.config';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-topbar',
  standalone: true,
  imports: [
    RouterModule,
    CommonModule,
    ImageModule,
    StyleClassModule,
    AppConfigurator,
    ButtonModule,
    LocationDropdownComponent,
    DiseaseDropdownComponent,
    DialogModule,
    SelectModule,
    FormsModule,
    PopoverModule,
  ],
  templateUrl: './app.topbar.html',
})
export class AppTopbar implements OnDestroy {
  items!: MenuItem[];

  private router = inject(Router);
  private http = inject(HttpClient);
  private locationSel = inject(LocationSelectionService);
  private loader = inject(LoadingService);
  private msal = inject(MsalService);
  private msalBroadcast = inject(MsalBroadcastService);
  private analysisStore = inject(AnalysisStore);
  private t3AnalysisStore = inject(T3AnalysisStore);
  private meService = inject(MeService);

  showGenerateButton = true;
  showLocationDropdown = true;
  showDiseaseDropdown = true;

  userLevel = '';

  infoDialogVisible = false;
  private onlyBaselinePresent = false;

  private analysisRuns: any[] = [];
  private t3Runs: any[] = [];
  private currentUrl = '';
  userName = '';
  userEmail = '';
  isAdminUser = false;

  private destroy$ = new Subject<void>();

  private onMeReady = () => this.hydrateFromSession();

  languageDialogVisible = false;
  languageOptions = [
    { label: 'English', value: 'English' },
    { label: 'Portuguese', value: 'Portuguese' },
  ];
  selectedLanguage: 'English' | 'Portuguese' = 'English';

  constructor(public layoutService: LayoutService) {
    this.msalBroadcast.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
        takeUntil(this.destroy$)
      )
      .subscribe((msg: EventMessage) => {
        const result = msg.payload as AuthenticationResult;
        this.persistIdentityFromResult(result);
        this.hydrateFromSession();
      });

    effect(() => {
      const _ = this.layoutService.accessDenied$();
      this.updateVisibility(this.router.url);
    });
  }

  async ngOnInit() {
    await this.ensureIdentity().catch(() => { });
    this.hydrateFromSession();

    window.addEventListener('me-data-ready', this.onMeReady);

    this.updateVisibility(this.router.url);

    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd), takeUntil(this.destroy$))
      .subscribe((e: any) => this.updateVisibility(e.urlAfterRedirects || e.url));

    this.analysisStore.runsList$
      .pipe(takeUntil(this.destroy$))
      .subscribe((runs) => {
        this.analysisRuns = Array.isArray(runs) ? runs : [];
        this.updateOnlyBaselinePresent();
      });

    this.t3AnalysisStore.runsList$
      .pipe(takeUntil(this.destroy$))
      .subscribe((runs) => {
        this.t3Runs = Array.isArray(runs) ? runs : [];
        this.updateOnlyBaselinePresent();
      });
  }

  private updateOnlyBaselinePresent(): void {
    const isT3 = this.isT3DashboardRoute(this.currentUrl || this.router.url);
    const runs = isT3 ? this.t3Runs : this.analysisRuns;

    if (!Array.isArray(runs) || runs.length === 0) {
      this.onlyBaselinePresent = true;
      return;
    }

    this.onlyBaselinePresent = !runs.some((run) => {
      return !this.isBaselineTitleForCurrentRoute(run?.title, isT3);
    });
  }

  private isBaselineTitleForCurrentRoute(title: string | undefined, isT3: boolean): boolean {
    const normalized = String(title || '').trim().toLowerCase();

    if (isT3) {
      return normalized === 'basic simulation' || normalized === 'baseline';
    }

    return (
      normalized === 'basic simulation' ||
      normalized === 'baseline' ||
      normalized === 'simulation 1'
    );
  }

  private updateVisibility(url: string) {
    this.currentUrl = url || this.router.url || '';

    const isAdminDashboard =
      this.currentUrl.startsWith('/admin-dashboard');

    const isHome = this.currentUrl.startsWith('/home');

    const hideDiseaseForRoute =
      isHome ||
      this.currentUrl.startsWith('/resources') ||
      isAdminDashboard;

    const accessDenied = this.layoutService.getAccessDenied();

    const isT6Dashboard = this.isT6DashboardRoute(this.currentUrl);
    const isT3Dashboard = this.isT3DashboardRoute(this.currentUrl);

    this.showGenerateButton = (isT6Dashboard || isT3Dashboard) && !accessDenied;
    this.showLocationDropdown = !isAdminDashboard && !isHome;
    this.showDiseaseDropdown = !hideDiseaseForRoute;

    this.updateOnlyBaselinePresent();
  }

  private normalize(s: string) {
    return (s || '').trim().replace(/\s+/g, ' ');
  }

  private isT3DashboardRoute(url: string = this.router.url): boolean {
    return String(url || '').startsWith('/enhanced-simulator');
  }

  private isT6DashboardRoute(url: string = this.router.url): boolean {
    return String(url || '').startsWith('/t6-dashboard');
  }

  private sanitizeName(raw: string): string {
    const parts = this.normalize(raw).split(' ').filter(Boolean);
    if (parts.length === 0) return '';
    const dedupSeq: string[] = [];
    for (const p of parts) {
      if (!dedupSeq.length || dedupSeq[dedupSeq.length - 1].toLowerCase() !== p.toLowerCase()) {
        dedupSeq.push(p);
      }
    }
    if (dedupSeq.length > 1 && dedupSeq[0].toLowerCase() === dedupSeq[dedupSeq.length - 1].toLowerCase()) {
      dedupSeq.pop();
    }
    return dedupSeq.join(' ');
  }

  private pickEmail(claims: any, account?: any): string {
    const preferred = this.normalize(claims?.preferred_username || '');
    if (preferred) return preferred;
    const emails = Array.isArray(claims?.emails) ? claims.emails : [];
    if (emails.length > 0) return this.normalize(emails[0] || '');
    return this.normalize(account?.username || '');
  }

  private pickName(claims: any, account?: any): string {
    const name = this.normalize(claims?.name || '');
    if (name) return this.sanitizeName(name);

    const given = this.normalize(claims?.given_name || '');
    const family = this.normalize(claims?.family_name || '');
    const composed = this.normalize([given, family].filter(Boolean).join(' '));
    if (composed) return this.sanitizeName(composed);

    return this.normalize(account?.username || '');
  }

  private persistIdentityFromResult(result: AuthenticationResult | null) {
    const account =
      result?.account ??
      (this.msal.instance.getActiveAccount() ?? this.msal.instance.getAllAccounts()[0]);
    const claims = result?.idTokenClaims as any;
    const email = this.pickEmail(claims, account);
    const name = this.pickName(claims, account);
    try {
      sessionStorage.setItem('userEmail', email || '');
      sessionStorage.setItem('userName', name || '');
    } catch { }
  }

  private hydrateFromSession() {
    this.userEmail = sessionStorage.getItem('userEmail') || '';
    this.userName = sessionStorage.getItem('userName') || '';
    this.userLevel = sessionStorage.getItem('userLevelFromMe') || '';
    this.isAdminUser =
      (sessionStorage.getItem('userAdminAccessFromMe') || '').trim().toLowerCase() === 'yes';
  }

  private async ensureIdentity() {
    if (sessionStorage.getItem('userEmail') || sessionStorage.getItem('userName')) return;

    const account = this.msal.instance.getActiveAccount() ?? this.msal.instance.getAllAccounts()[0];
    if (!account) return;

    let claims: any = (account as any).idTokenClaims || null;
    if (!claims) {
      try {
        const res = await this.msal.instance.acquireTokenSilent({
          authority: authoritySUSI,
          account,
          scopes: ['openid', 'profile'],
        });
        claims = res.idTokenClaims;
      } catch {
        claims = null;
      }
    }

    const email = this.pickEmail(claims, account);
    const name = this.pickName(claims, account);
    try {
      sessionStorage.setItem('userEmail', email || '');
      sessionStorage.setItem('userName', name || '');
    } catch { }
  }

  viewProfile() {
    this.router.navigate(['/home']);
  }
  openAnalytics() {
    this.router.navigate(['/economy-analysis']);
  }
  openHelpCenter() {
    this.router.navigate(['/resources']);
  }
  openAccountSettings() {
    this.router.navigate(['/about']);
  }
  openAdminDashboard() {
    this.router.navigate(['/admin-dashboard']);
  }

  toggleDarkMode() {
    this.layoutService.layoutConfig.update((state) => ({
      ...state,
      darkTheme: !state.darkTheme,
    }));
  }

  logout() {
    try {
      this.meService.clearCache();
    } catch { }

    try {
      sessionStorage.clear();
    } catch { }

    try {
      localStorage.removeItem('simulations_list');
      localStorage.removeItem('analysis_runs');
      localStorage.removeItem('selected_run_id');
      localStorage.removeItem('userName');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('t0_runs_v1');
      localStorage.removeItem('t0_selected_run_id_v1');
      localStorage.removeItem('t3_runs_v1');
      localStorage.removeItem('t3_selected_run_id_v1');
      localStorage.removeItem('session_id');
      localStorage.removeItem('me_cache_v1');
    } catch { }

    try {
      this.analysisStore.clearAll?.();
    } catch { }

    this.userName = '';
    this.userEmail = '';
    this.userLevel = '';

    const postLogoutRedirectUri = `${window.location.origin}/login`;
    this.msal.logoutRedirect({ postLogoutRedirectUri });
  }

  requestSummaryDocx(lang: 'English' | 'Portuguese' = 'English'): void {
    const url = this.getSummaryApiUrl();
    const loc = this.locationSel?.selected?.() || null;
    const isT3 = this.isT3DashboardRoute(this.currentUrl || this.router.url);

    const country = isT3
      ? 'Singapore'
      : loc?.country || 'Brazil';

    const city = isT3
      ? 'Singapore'
      : loc?.city || 'Sao Paulo';

    const sessionKey = 'session_id';
    let sessionId = sessionStorage.getItem(sessionKey);
    if (!sessionId) {
      sessionId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(sessionKey, sessionId);
    }

    const userId = this.userEmail || 'guest';

    const payload = {
      City: city,
      Country: country,
      User_ID: userId,
      Session_ID: sessionId,
      Language: lang,
    };

    this.http.post(url, payload, { responseType: 'blob' }).subscribe({
      next: (blob) => {
        this.loader.hide();
        const filename = 'summary.docx';
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(objectUrl);
      },
      error: (err) => {
        this.loader.hide();
        console.error('Summary request failed', err);
      },
    });
  }

  confirmGenerate(): void {
    this.languageDialogVisible = false;

    if (this.onlyBaselinePresent) {
      this.infoDialogVisible = true;
      return;
    }
    this.loader.show('Generating...');
    this.requestSummaryDocx(this.selectedLanguage);
  }

  openLanguageDialog(): void {
    if (this.onlyBaselinePresent) {
      this.infoDialogVisible = true;
    } else {
      this.languageDialogVisible = true;
    }
  }

  ngOnDestroy() {
    window.removeEventListener('me-data-ready', this.onMeReady);
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getSummaryApiUrl(): string {
    const isT3 = this.isT3DashboardRoute(this.currentUrl || this.router.url);

    if (isT3) {
      return `${environment.apiUrl}/summary_t3`;
    }

    return (environment as any)?.apiSummaryUrl || `${environment.apiUrl}/summary`;
  }
}